import PropTypes from 'prop-types';
import {
    Box, Typography, Stack, TextField,
} from '@mui/material';
import Iconify from 'src/components/Iconify';

// ─── Shared input styles ─────────────────────────────────────────────────────

const INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '8px', fontSize: '13px', bgcolor: '#fff',
        '& fieldset': { borderColor: 'rgba(145,158,171,0.2)' },
        '&.Mui-focused fieldset': { borderColor: '#00AB55', borderWidth: '1.5px' },
        alignItems: 'flex-start',
    },
    '& .MuiOutlinedInput-input': { py: '8px', px: '10px' },
};

// ─── Section wrapper ─────────────────────────────────────────────────────────

function Section({ icon, iconColor, title, subtitle, children }) {
    return (
        <Box
            sx={{
                bgcolor: '#fff', borderRadius: '14px',
                border: '1px solid rgba(145,158,171,0.16)',
                px: 2.5, pt: 2, pb: 2.5, mb: 2.5,
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <Iconify icon={icon} width={16} sx={{ color: iconColor }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#637381', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {title}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '10px' }}>
                        · {subtitle}
                    </Typography>
                )}
            </Stack>
            {children}
        </Box>
    );
}

Section.propTypes = {
    icon: PropTypes.string.isRequired,
    iconColor: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node.isRequired,
};
Section.defaultProps = { subtitle: '' };

// ─── Main export ─────────────────────────────────────────────────────────────

export default function TreatmentPlanVisualsNotes({
    thumbnailUrl,
    onThumbnailChange,
    patientNotes,
    onPatientNotesChange,
    options,
    onChange,
}) {
    // Update included/excluded on options
    const updateOption = (idx, field, value) => {
        const updated = [...options];
        updated[idx] = { ...updated[idx], [field]: value };
        onChange(updated);
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Iconify icon="eva:image-fill" width={20} sx={{ color: '#00AB55' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '17px' }}>
                        Visuals & Notes
                    </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13px' }}>
                    Add a hero image, notes to the patient, and clarify what is and isn&apos;t included
                </Typography>
            </Box>

            {/* Thumbnail / Hero Image */}
            <Section icon="eva:image-fill" iconColor="#078DEE" title="Hero Image" subtitle="Optional — shows at the top of the patient view">
                <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#637381', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Image URL
                    </Typography>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="https://example.com/treatment-image.jpg"
                        value={thumbnailUrl || ''}
                        onChange={(e) => onThumbnailChange(e.target.value)}
                        sx={INPUT_SX}
                    />
                    <Typography variant="caption" sx={{ fontSize: '10px', color: 'text.disabled', mt: 0.5, display: 'block' }}>
                        Paste a URL to a before/after photo or treatment illustration
                    </Typography>
                </Box>

                {/* Image preview */}
                {thumbnailUrl && (
                    <Box
                        sx={{
                            borderRadius: '10px',
                            overflow: 'hidden',
                            border: '1px solid rgba(145,158,171,0.12)',
                            maxHeight: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#F4F6F8',
                        }}
                    >
                        <Box
                            component="img"
                            src={thumbnailUrl}
                            alt="Treatment plan hero"
                            onError={(e) => { e.target.style.display = 'none'; }}
                            sx={{
                                maxWidth: '100%', maxHeight: 200,
                                objectFit: 'cover',
                            }}
                        />
                    </Box>
                )}
            </Section>

            {/* Patient Notes */}
            <Section icon="eva:message-square-fill" iconColor="#FFAB00" title="Note to Patient" subtitle="Shown at the top of the treatment plan">
                <TextField
                    size="small"
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Write a personal note to the patient — e.g. 'Thank you for visiting us today. We've put together this treatment plan based on our consultation. Please review it and let us know if you have any questions.'"
                    value={patientNotes || ''}
                    onChange={(e) => onPatientNotesChange(e.target.value)}
                    sx={INPUT_SX}
                />
            </Section>

            {/* Included / Excluded per option */}
            <Section icon="eva:list-fill" iconColor="#00AB55" title="What's Included & Excluded" subtitle="Per option">
                {options.map((opt, idx) => (
                    <Box key={opt.id} sx={{ mb: idx < options.length - 1 ? 2.5 : 0 }}>
                        {options.length > 1 && (
                            <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '11px', color: '#212B36', mb: 1, display: 'block' }}>
                                {opt.name || `Option ${idx + 1}`}
                            </Typography>
                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#00AB55', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Included
                                </Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="List everything covered by the quoted price..."
                                    value={opt.included || ''}
                                    onChange={(e) => updateOption(idx, 'included', e.target.value)}
                                    sx={INPUT_SX}
                                />
                            </Box>
                            <Box>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#FF4842', fontSize: '11px', mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Not included
                                </Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="List any exclusions or potential additional costs..."
                                    value={opt.excluded || ''}
                                    onChange={(e) => updateOption(idx, 'excluded', e.target.value)}
                                    sx={INPUT_SX}
                                />
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Section>
        </Box>
    );
}

TreatmentPlanVisualsNotes.propTypes = {
    thumbnailUrl: PropTypes.string,
    onThumbnailChange: PropTypes.func.isRequired,
    patientNotes: PropTypes.string,
    onPatientNotesChange: PropTypes.func.isRequired,
    options: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
};

TreatmentPlanVisualsNotes.defaultProps = {
    thumbnailUrl: '',
    patientNotes: '',
};
